import React, { useState } from 'react';
import { Container, Grid, Typography, Tab, Tabs, Paper, useTheme } from '@mui/material';
import { PlasticRoute, plasticRoutesData, plasticCategories } from '../../types';
import PlasticRouteCard from '../plastic/PlasticRouteCard';
import PlasticFlowDiagram from '../plastic/PlasticFlowDiagram';
import SwipeableFlowShell from '../common/SwipeableFlowShell';
import CategoryTabLabel from '../common/CategoryTabLabel';

const PlasticView: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRoute, setSelectedRoute] = useState<PlasticRoute | null>(
  () => plasticRoutesData.find((route) => route.category === plasticCategories[0].category) ?? null
);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const newCategory = plasticCategories[newValue];
    const firstRoute = plasticRoutesData.find((route) => route.category === newCategory.category);
    setSelectedRoute(firstRoute ?? null);
  };

  const handleRouteSelect = (route: PlasticRoute) => {
    setSelectedRoute(route);
  };

  const filteredRoutes = plasticRoutesData.filter((route) => {
    const category = plasticCategories[activeTab];
    return route.category === category.category;
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: isDarkMode ? '#1b5e20' : '#e8f5e9',
          borderRadius: 2,
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            color: isDarkMode ? '#66bb6a' : '#2e7d32', 
            fontWeight: 600 
          }}
        >
          塑料制备路线
        </Typography>
        <Typography variant="body1" color="text.secondary">
          涵盖高密度聚乙烯(HDPE)、低密度聚乙烯(LDPE)、超高分子量聚乙烯(UHMWPE)、聚丙烯(PP)、聚氯乙烯(PVC)、聚对苯二甲酸乙二醇酯(PET)、聚苯乙烯(PS)、ABS树脂等主要塑料品种的制备工艺路线
        </Typography>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
            },
            '& .Mui-selected': {
              backgroundColor: isDarkMode ? '#2e7d3230' : '#e8f5e9',
            },
          }}
        >
          {plasticCategories.map((category, categoryIndex) => (
            <Tab
              key={category.id}
              label={
                <CategoryTabLabel
                  label={category.label}
                  routes={
                    plasticRoutesData
                      .filter((route) => route.category === category.category)
                      .map((route) => ({ id: route.id, name: route.name }))
                  }
                  selectedRouteId={selectedRoute?.id}
                  onSelectRoute={(routeId) => {
                    handleTabChange(null as unknown as React.SyntheticEvent, categoryIndex);
                    const target = plasticRoutesData.find((route) => route.id === routeId);
                    if (target) setSelectedRoute(target);
                  }}
                />
              }
            />
          ))}
        </Tabs>
      </Paper>

      <Grid container spacing={3}>
        {selectedRoute && (
          <Grid item xs={12}>
            <SwipeableFlowShell
              routes={filteredRoutes}
              selectedId={selectedRoute.id}
              onSelect={(id) => setSelectedRoute(filteredRoutes.find((r) => r.id === id) ?? null)}
            >
              <PlasticFlowDiagram route={selectedRoute} />
            </SwipeableFlowShell>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              color: isDarkMode ? '#66bb6a' : '#2e7d32', 
              fontWeight: 600 
            }}
          >
            {plasticCategories[activeTab].label} - 制备工艺
          </Typography>
          <Grid container spacing={2}>
            {filteredRoutes.map((route) => (
              <Grid item xs={12} sm={6} lg={4} key={route.id}>
                <PlasticRouteCard
                  route={route}
                  onClick={handleRouteSelect}
                  isSelected={selectedRoute?.id === route.id}
                />
              </Grid>
            ))}
          </Grid>
          {filteredRoutes.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5' }}>
              <Typography color="text.secondary">
                该分类下暂无技术路线数据
              </Typography>
            </Paper>
          )}
        </Grid>

      </Grid>

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 3,
          backgroundColor: isDarkMode ? '#4a3728' : '#fff3e0',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: isDarkMode ? '#ffb74d' : '#ff9800',
            fontWeight: 600
          }}
        >
          使用提示
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • 点击下方路线卡片查看详细工艺流程图
          <br />
          • 在流程图中点击工艺步骤可高亮显示当前步骤
          <br />
          • 使用鼠标滚轮或控制按钮缩放流程图
          <br />• 拖动流程图可查看完整工艺流程
        </Typography>
      </Paper>
    </Container>
  );
};

export default PlasticView;
